import React, { useCallback, useMemo, useRef } from 'react';

import type { SuggestedEditKind, UUID } from '@aline/common';
import { Select } from '@aline/common';
import { closestCenter, DndContext } from '@dnd-kit/core';
import { SortableContext, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { IconButton, Typography } from '@mui/material';
import { CirclePlus, ListOrdered, Sparkles } from 'lucide-react';
import { AlineTooltip } from 'src/components';
import { AlineAlert } from 'src/components/generic';
import { InlineEditField } from 'src/components/generic/InlineEditInput';
import { useUpdatePlaybookRule } from 'src/hooks';
import { makeStyles } from 'tss-react/mui';

import { createEditRulePrompt } from './edit-playbook-prompt';
import { PlaybookAddButton } from './playbook-add-button';
import { PositionCard } from './PositionCard';
import { useRuleDetailsStyles } from './rule-details-styles';
import { getRuleRunWarning, RULE_RUN_WARNING_MESSAGES } from './rule-run-warning';
import {
  findStop,
  interleaveByRank,
  MoveArrowOverlay,
  SuggestedTextDiff,
  SUGGESTION_CARD_TONES,
  SuggestionDecision,
  SuggestionPill,
  useMoveHover,
  useStopDecision,
  useSuggestionReview,
} from './suggested-edits';
import type { SuggestionStop } from './suggested-edits';
import type { PlaybookDetailRule } from './types';
import { useRulePositions } from './use-rule-positions';
import { useDraftPromptInOpenThread } from '../use-draft-prompt-in-new-chat';

/** The border around the details of a rule pending as a whole. */
const PENDING_BORDER_PX = 2;

const useSuggestionStyles = makeStyles<{
  /** The kind the whole rule is pending as: an add or a delete, else null. */
  pendingKind: SuggestedEditKind | null;
  /** The rule's title edit is the current stop of the suggestions bar. */
  titleCurrent: boolean;
}>()((theme, { pendingKind, titleCurrent }) => {
  const pendingTone = pendingKind ? SUGGESTION_CARD_TONES[pendingKind] : null;
  return {
    /**
     * A rule being added or removed as a whole, by its own decision or its
     * category's, sits in a rounded box tinted like the matching category
     * card on the left, under a thick border in the kind's darker shade: the
     * same weight and colour as the ring on the rule's selected row, which is
     * the row that opened this panel. The box is inset from the column and
     * its padding shrinks by the border's width, so the contents sit where
     * they do for any other rule and nothing jumps between rules.
     */
    contentPending: {
      backgroundColor: pendingTone?.background,
      border: `${PENDING_BORDER_PX}px solid ${pendingTone?.currentBorder}`,
      borderRadius: 12,
      margin: theme.spacing(1.5),
      padding: `calc(${theme.spacing(1.5)} - ${PENDING_BORDER_PX}px)`,
    },
    /**
     * The title row while its edit is the current stop: the same treatment
     * as the playbook title, the change tint under a 2px ring in its border
     * colour. The ring is an outline, which follows the corner radius and
     * takes no layout space; the transparent border and the negative margin
     * with matching padding keep the text where it sits when the ring is off.
     */
    titleRowCurrent: {
      backgroundColor: titleCurrent ? SUGGESTION_CARD_TONES.change.background : 'transparent',
      border: '1px solid transparent',
      borderRadius: 8,
      margin: theme.spacing(-0.5),
      outline: titleCurrent ? `2px solid ${SUGGESTION_CARD_TONES.change.border}` : undefined,
      padding: theme.spacing(0.5),
    },
  };
});

/**
 * The rule's title: its diff while a title edit is pending (read-only until
 * the suggestion resolves), plain text while the rule is pending as a whole,
 * and the editable field otherwise.
 */
const RuleTitle = React.memo(function RuleTitle({
  disabled,
  initiallyEditing,
  readOnly,
  rule,
  suggestedTitle,
}: {
  disabled: boolean;
  initiallyEditing: boolean;
  readOnly: boolean;
  rule: PlaybookDetailRule;
  suggestedTitle: string | undefined;
}): JSX.Element {
  const { classes, cx } = useRuleDetailsStyles();
  const { updatePlaybookRule } = useUpdatePlaybookRule();
  /**
   * An emptied field leaves the rule unnamed rather than putting the old name
   * back: the field already committed the trimmed text, so the empty string
   * is what "nobody has named this" is stored as.
   */
  const handleSave = useCallback(
    (title: string) => updatePlaybookRule({ props: { title }, ruleId: rule.id }),
    [rule.id, updatePlaybookRule]
  );

  if (suggestedTitle !== undefined) {
    return (
      <SuggestedTextDiff
        after={suggestedTitle}
        before={rule.title}
        className={classes.titleDisplay}
      />
    );
  }

  if (readOnly) {
    return (
      <div className={cx(classes.titleDisplay, !rule.title && classes.titlePlaceholder)}>
        {rule.title || 'Untitled rule'}
      </div>
    );
  }

  return (
    <InlineEditField
      multiline
      classes={{
        display: classes.titleDisplay,
        icon: classes.titleEditIcon,
        input: classes.titleInput,
        placeholder: classes.titlePlaceholder,
      }}
      disabled={disabled}
      editTooltip="Rename rule"
      iconAlign="start"
      iconSize={16}
      /**
       * A rule created from "Add rule" opens here with nothing in it, so
       * the caret starts at its name. The panel is keyed by rule id, so
       * this fires once per newly opened rule rather than on every render.
       */
      initiallyEditing={initiallyEditing}
      placeholder="Type a rule title..."
      textPlaceholder="Untitled rule"
      value={rule.title}
      onSave={handleSave}
    />
  );
});

/**
 * The destination end of a position move: a dashed move pill in the target
 * slot, filled in the move tone while the move is the current stop. The arrow
 * overlay pairs this row with the source.
 */
const PositionGhostRow = React.memo(function PositionGhostRow({
  positionId,
  stop,
}: {
  positionId: UUID;
  stop: SuggestionStop;
}): JSX.Element {
  const { classes } = useRuleDetailsStyles();
  const { currentStop } = useSuggestionReview();
  const decision = useStopDecision(stop);
  const hover = useMoveHover(positionId);

  return (
    <div
      className={classes.positionGhostRow}
      data-cy={Select.playbooks.suggestionGhost}
      data-move-anchor={`${positionId}:ghost`}
      onMouseEnter={hover.onMouseEnter}
      onMouseLeave={hover.onMouseLeave}
    >
      <SuggestionPill
        centered
        dashed
        acceptDisabledReason={stop.blockedReason}
        acceptTitle="Accept move"
        current={currentStop?.key === stop.key}
        kind="moveDestination"
        label="Final position"
        rationale={stop.rationale}
        rejectTitle="Reject move"
        onAccept={decision.onAccept}
        onReject={decision.onReject}
        onSelect={decision.onSelect}
      />
    </div>
  );
});

/**
 * Drafts an edit request for this rule into the open thread, or a new chat
 * when none is open, like the sparkle in the playbook header. The user
 * finishes the sentence and sends it; the planner routes it to the playbook
 * editor, whose edits come back as suggestions on this page.
 */
const EditRuleWithAiButton = React.memo(function EditRuleWithAiButton({
  disabled,
  rule,
}: {
  disabled: boolean;
  rule: PlaybookDetailRule;
}): JSX.Element {
  const { classes } = useRuleDetailsStyles();
  const draftPrompt = useDraftPromptInOpenThread();
  const handleEditWithAi = useCallback(() => {
    draftPrompt(createEditRulePrompt({ ruleId: rule.id, ruleTitle: rule.title }));
  }, [draftPrompt, rule.id, rule.title]);

  return (
    <AlineTooltip arrow placement="top" title="Edit rule with AI">
      <IconButton
        aria-label="Edit rule with AI"
        className={classes.titleAction}
        data-cy={Select.playbooks.ruleEditWithAiButton}
        disabled={disabled}
        size="small"
        onClick={handleEditWithAi}
      >
        <Sparkles size={16} />
      </IconButton>
    </AlineTooltip>
  );
});

interface RuleDetailsPanelProps {
  playbookId: string;
  rule: PlaybookDetailRule;
  disabled?: boolean;
}

/**
 * Editable details for the selected rule: its positions as one ordered,
 * reorderable list. Index 0 is the Starting Position and the rest are its
 * fallbacks, so dragging a fallback to the top promotes it. Each field commits
 * on blur straight to the position it edits — see `useRulePositions`, which owns
 * every one of those writes.
 *
 * Pending AI suggestions render on top: a title diff in place, a pill for a
 * rule-level add or delete, per-position pills and diffs, and ghost cards at
 * the destination of pending moves.
 */
export function RuleDetailsPanel({
  disabled: disabledProp = false,
  playbookId,
  rule,
}: RuleDetailsPanelProps): JSX.Element {
  const { classes, cx } = useRuleDetailsStyles();
  const { activeMoveId, currentStop, model, rejectPendingMove } = useSuggestionReview();
  const panelRef = useRef<HTMLDivElement | null>(null);
  const {
    addFallback,
    addedPositionId,
    commitPosition,
    deletePosition,
    handleDragEnd,
    isEmptyRule,
    positions,
    sensors,
  } = useRulePositions(rule, { onBeforeDrop: rejectPendingMove });

  const ruleEdit = model.ruleEdits.get(rule.id) ?? null;
  const ruleStop = findStop(model, rule.id, 'add') ?? findStop(model, rule.id, 'delete');
  // A rule patch can only touch the title, so this stop is the title edit.
  const titleStop = findStop(model, rule.id, 'patch');
  // An add or delete cannot coexist with a patch, so at most one of these is set.
  const titleRowStop = titleStop ?? ruleStop;
  const titleRowDecision = useStopDecision(titleRowStop);
  const handleAddFallback = useCallback(() => void addFallback(), [addFallback]);
  /**
   * The rule is pending as a whole, added or removed: by its own edit, or by
   * its category's. A rule born with a new category carries its own add
   * flag, but a rule in a category proposed for removal carries nothing, so
   * the category's edit is read too.
   */
  const categoryId = model.ruleCategoryIds.get(rule.id);
  const categoryEdit = categoryId ? (model.categoryEdits.get(categoryId) ?? null) : null;
  const pendingRuleKind = getWholeEditKind(ruleEdit) ?? getWholeEditKind(categoryEdit);
  /**
   * A rule pending as a whole, added or removed, is read-only until that is
   * decided: no renaming, no editing, deleting or dragging positions. The
   * suggestion's own accept/reject stays live; it is the only thing to do.
   * Read-only rather than disabled, so the text keeps its normal colour: the
   * rule is being proposed, not switched off.
   */
  const readOnly = Boolean(pendingRuleKind);
  const disabled = disabledProp;
  const isTitleCurrent = Boolean(titleStop && currentStop?.key === titleStop.key);
  const { classes: suggestionClasses } = useSuggestionStyles({
    pendingKind: pendingRuleKind,
    titleCurrent: isTitleCurrent,
  });
  const suggestedTitle = ruleEdit?.type === 'change' ? ruleEdit.patch?.value.title : undefined;

  /** Positions and the ghost destinations of pending position moves, in rank order. */
  const runWarning = getRuleRunWarning(rule);
  const entries = useMemo(() => {
    const ghosts = positions.flatMap(position => {
      const edit = model.positionEdits.get(position.id);
      return edit?.type === 'change' && edit.move ? [{ position, rank: edit.move.rank }] : [];
    });
    return interleaveByRank(positions, ghosts);
  }, [model.positionEdits, positions]);

  return (
    <div ref={panelRef} className={classes.panel} data-cy={Select.playbooks.ruleDetails}>
      <div
        className={cx(
          classes.panelContent,
          Boolean(pendingRuleKind) && suggestionClasses.contentPending
        )}
      >
        <div className={cx(classes.titleRow, suggestionClasses.titleRowCurrent)}>
          <div className={classes.titleField} data-cy={Select.playbooks.ruleTitle}>
            <RuleTitle
              disabled={disabled}
              initiallyEditing={isEmptyRule}
              readOnly={readOnly}
              rule={rule}
              suggestedTitle={suggestedTitle}
            />
          </div>
          {titleRowStop ? (
            /**
             * The same chip and decision the rule's row offers, so the edit can
             * be settled from either side: a title edit, or the whole rule's add
             * or delete. Top-aligned, so a wrapped title keeps it on its first
             * line and flows on beneath.
             */
            <SuggestionDecision
              acceptDisabledReason={titleRowStop.blockedReason}
              className={classes.titleDecision}
              kind={titleRowStop.kind}
              rationale={titleRowStop.rationale}
              onAccept={titleRowDecision.onAccept}
              onReject={titleRowDecision.onReject}
              onSelect={titleRowDecision.onSelect}
            />
          ) : (
            /**
             * Like the playbook header, the sparkle gives way to a pending
             * decision, and goes while the rule is pending as a whole: the
             * suggestion is the only thing to decide until it resolves.
             */
            !readOnly && <EditRuleWithAiButton disabled={disabled} rule={rule} />
          )}
        </div>

        {runWarning && (
          <AlineAlert
            data-cy={Select.playbooks.ruleRunWarning}
            severity="warning"
            variant="outlined"
          >
            {RULE_RUN_WARNING_MESSAGES[runWarning]}
          </AlineAlert>
        )}

        <div>
          <div className={classes.positionsHeader}>
            <Typography className={classes.positionsTitle} variant="subtitle1">
              <ListOrdered size={16} />
              Positions
            </Typography>
            {/* A rule added or removed as a whole is decided as a whole, so nothing
                is added to it until then. Both add controls go. */}
            {!pendingRuleKind && (
              <AlineTooltip arrow placement="top" title="Add a new position">
                <IconButton
                  className={classes.addHeaderButton}
                  data-cy={Select.playbooks.addPositionHeader}
                  disabled={disabled}
                  size="small"
                  onClick={handleAddFallback}
                >
                  <CirclePlus size={20} />
                </IconButton>
              </AlineTooltip>
            )}
          </div>

          <DndContext
            collisionDetection={closestCenter}
            sensors={sensors}
            onDragEnd={handleDragEnd}
          >
            <SortableContext
              items={positions.map(position => position.id)}
              strategy={verticalListSortingStrategy}
            >
              <div className={classes.positionList}>
                {entries.map(entry => {
                  if (entry.item) {
                    const position = entry.item;
                    return (
                      <PositionCard
                        key={position.id}
                        autoFocus={position.id === addedPositionId}
                        containerKind={pendingRuleKind}
                        disabled={disabled}
                        // Counts real positions only: a ghost destination takes no number.
                        index={positions.indexOf(position)}
                        playbookId={playbookId}
                        position={position}
                        readOnly={readOnly}
                        ruleId={rule.id}
                        onCommit={commitPosition}
                        onDelete={deletePosition}
                      />
                    );
                  }
                  const ghost = entry.ghost;
                  if (!ghost) return null;
                  const stop = findStop(model, ghost.position.id, 'move');
                  if (!stop) return null;
                  return (
                    <PositionGhostRow
                      key={`ghost-${ghost.position.id}`}
                      positionId={ghost.position.id}
                      stop={stop}
                    />
                  );
                })}
              </div>
            </SortableContext>
          </DndContext>

          {!pendingRuleKind && (
            <div className={classes.addPositionRow}>
              <PlaybookAddButton
                dataCy={Select.playbooks.addPosition}
                disabled={disabled}
                onClick={handleAddFallback}
              >
                Add Position
              </PlaybookAddButton>
            </div>
          )}
        </div>
      </div>
      <MoveArrowOverlay activeMoveId={activeMoveId} containerRef={panelRef} />
    </div>
  );
}

/** An add or delete, which covers the whole entity; a change touches only parts of it. */
function getWholeEditKind(edit: { type: SuggestedEditKind } | null): SuggestedEditKind | null {
  return edit?.type === 'add' || edit?.type === 'delete' ? edit.type : null;
}
